import { useMemo, useState } from "react";
import { Search, Download } from "lucide-react";

const STATUS_STYLES = {
  paid: "bg-emerald-100 text-emerald-800",
  confirmed: "bg-emerald-100 text-emerald-800",
  registered: "bg-emerald-100 text-emerald-800",
  completed: "bg-sky-100 text-sky-800",
  cancelled: "bg-red-100 text-red-700",
  pending: "bg-amber-100 text-amber-800",
  created: "bg-amber-100 text-amber-800",
};

export function StatusPill({ s }) {
  return <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize ${STATUS_STYLES[s] || "bg-gray-100 text-gray-700"}`}>{s || "-"}</span>;
}

export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "-");

function toCsv(rows, columns) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const head = columns.map((c) => esc(c.label)).join(",");
  const body = rows.map((r) => columns.map((c) => esc(c.csv ? c.csv(r) : r[c.key])).join(",")).join("\n");
  return `${head}\n${body}`;
}

export function DataTable({ rows, columns, testid, title, filename }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows || [];
    return (rows || []).filter((r) => JSON.stringify(r).toLowerCase().includes(s));
  }, [rows, q]);

  const download = () => {
    const blob = new Blob([toCsv(filtered, columns)], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${filename}.csv`;
    a.click();
  };

  return (
    <div className="rounded-3xl bg-white border border-[#D7D4CF]/70 overflow-hidden" data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-3 p-5 border-b border-[#D7D4CF]/60">
        <h2 className="font-editorial text-2xl text-[#1D3A2F]">{title} <span className="text-base text-[#556B57]">({filtered.length})</span></h2>
        <div className="flex gap-2">
          <label className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#556B57]" />
            <input data-testid={`${testid}-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="h-10 pl-9 pr-4 rounded-full border border-[#D7D4CF] text-sm focus:outline-none focus:border-[#C9A961]" />
          </label>
          <button onClick={download} data-testid={`${testid}-export`} className="h-10 px-4 rounded-full border border-[#D7D4CF] text-sm inline-flex items-center gap-2 hover:border-[#C9A961]"><Download className="w-4 h-4" /> CSV</button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F4EE] text-left">
            <tr>{columns.map((c) => <th key={c.label} className="px-4 py-3 text-[10px] tracking-[0.16em] uppercase text-[#556B57] font-semibold whitespace-nowrap">{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {!rows && <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-[#556B57]">Loading…</td></tr>}
            {rows && !filtered.length && <tr><td colSpan={columns.length} data-testid={`${testid}-empty`} className="px-4 py-10 text-center text-[#556B57]">No records yet.</td></tr>}
            {filtered.map((r, i) => (
              <tr key={r.id || i} className="border-t border-[#D7D4CF]/50 hover:bg-[#FDFBF7] align-top">
                {columns.map((c) => <td key={c.label} className="px-4 py-3 text-[#1D3A2F]">{c.render ? c.render(r) : (r[c.key] ?? "-")}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
